import { Badge, Button, Menu, Table, Text } from '@ui/themed';
import { t } from 'i18next';
import { useHandlers } from '../../../context/page-context';
import { dateTimeFormatter } from '../../../utils/format-date';
import { useDownloadTheme, usePublishTheme } from '../hooks/use-themes';
import type { Theme } from '../types/theme';

export function ThemesTable({
  themes,
  onRename,
  onUpload,
  onDelete,
}: {
  themes: Theme[];
  onRename: (theme: Theme) => void;
  onUpload: (theme: Theme) => void;
  onDelete: (theme: Theme) => void;
}) {
  const { navigationHandler } = useHandlers();
  const publishTheme = usePublishTheme();
  const downloadTheme = useDownloadTheme();

  const headings = [
    t('features:themes.pages.themes_list_page.table.headings.name'),
    t('features:themes.pages.themes_list_page.table.headings.updated'),
    '',
  ];

  return (
    <Table caption={t('features:themes.pages.themes_list_page.table.caption')} headings={headings}>
      {themes.map((theme, i) => (
        <Table.Row key={theme.id} id={theme.id} index={i}>
          <Table.Cell>
            <span className="flex items-center gap-2">
              <Text>{theme.name}</Text>
              {theme.is_published && (
                <Badge variant="green">
                  {t('features:themes.pages.themes_list_page.table.published')}
                </Badge>
              )}
            </span>
          </Table.Cell>
          <Table.Cell>
            <Text variant="secondary">{dateTimeFormatter.format(new Date(theme.updated_at))}</Text>
          </Table.Cell>
          <Table.Cell sticky>
            <span className="flex items-center justify-end gap-2">
              <Button
                variant="secondary"
                icon="document"
                onClick={() =>
                  navigationHandler.navigate(`/configuration/theme/editor/${theme.id}`)
                }
              >
                {t('features:themes.pages.themes_list_page.menu.edit_code')}
              </Button>
              <Menu
                collapsible
                align="end"
                buttonText={t('buttons:more_actions')}
                items={[
                  {
                    key: 'publish',
                    label: t('features:themes.pages.themes_list_page.menu.publish'),
                    icon: 'increase',
                    disabled: theme.is_published || publishTheme.isPending,
                    onClick: () => publishTheme.mutate(theme.id),
                  },
                  {
                    key: 'rename',
                    label: t('features:themes.pages.themes_list_page.menu.rename'),
                    icon: 'edit',
                    onClick: () => onRename(theme),
                  },
                  {
                    key: 'download',
                    label: t('features:themes.pages.themes_list_page.menu.download'),
                    icon: 'download',
                    disabled: downloadTheme.isPending,
                    onClick: () => downloadTheme.mutate(theme),
                  },
                  {
                    key: 'upload',
                    label: t('features:themes.pages.themes_list_page.menu.upload'),
                    icon: 'upload',
                    onClick: () => onUpload(theme),
                  },
                  {
                    key: 'delete',
                    label: t('features:themes.pages.themes_list_page.menu.delete'),
                    icon: 'delete',
                    danger: true,
                    disabled: theme.is_published,
                    onClick: () => onDelete(theme),
                  },
                ]}
              />
            </span>
          </Table.Cell>
        </Table.Row>
      ))}
    </Table>
  );
}
